/**
 * SessionExpiryWarning component for displaying session TTL warnings
 * Shows countdown timer with color coding based on urgency
 */
import { Badge } from '@/components/ui/badge'
import { Clock, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SessionExpiryWarningProps {
  /** When the session expires (ISO date string) */
  expiresAt?: string
  /** Always show warning regardless of TTL (for testing/preview) */
  showAlways?: boolean
  /** Display variant */
  variant?: 'banner' | 'badge'
  /** Additional CSS classes */
  className?: string
}

/**
 * Calculate time remaining until expiry
 */
function getTimeRemaining(expiresAt: string): {
  totalMinutes: number
  hours: number
  minutes: number
  isExpired: boolean
} {
  const now = new Date()
  const expiry = new Date(expiresAt)
  const diffMs = expiry.getTime() - now.getTime()
  
  if (diffMs <= 0) {
    return { totalMinutes: 0, hours: 0, minutes: 0, isExpired: true }
  }

  const totalMinutes = Math.floor(diffMs / (1000 * 60))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return { totalMinutes, hours, minutes, isExpired: false }
}

/**
 * Format time remaining for display
 */
function formatTimeRemaining(hours: number, minutes: number): string {
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

/**
 * Determine urgency level based on minutes remaining
 */
function getUrgencyLevel(totalMinutes: number): 'normal' | 'warning' | 'critical' {
  if (totalMinutes <= 30) return 'critical'
  if (totalMinutes <= 120) return 'warning'
  return 'normal'
}

/**
 * Warning banner or badge for session expiry.
 * 
 * - Shows when TTL < 2 hours (warning: yellow)
 * - Shows critical when TTL < 30 minutes (critical: red)
 * - Hidden when TTL >= 2 hours (unless showAlways is true)
 * 
 * @example
 * ```tsx
 * // Banner variant (full width alert)
 * <SessionExpiryWarning expiresAt={session.expiresAt} variant="banner" />
 * 
 * // Badge variant (inline)
 * <SessionExpiryWarning expiresAt={session.expiresAt} variant="badge" />
 * ```
 */
export function SessionExpiryWarning({
  expiresAt,
  showAlways = false,
  variant = 'banner',
  className
}: SessionExpiryWarningProps) {
  // Handle undefined or missing expiresAt
  if (!expiresAt) {
    return null
  }

  const { totalMinutes, hours, minutes, isExpired } = getTimeRemaining(expiresAt)
  const urgency = getUrgencyLevel(totalMinutes)

  // Hide if not urgent and not forced to show
  if (urgency === 'normal' && !showAlways) {
    return null
  }

  const timeDisplay = isExpired ? 'Expired' : formatTimeRemaining(hours, minutes)
  const isCritical = urgency === 'critical' || isExpired

  // Badge variant
  if (variant === 'badge') {
    return (
      <Badge
        variant={isCritical ? 'destructive' : 'warning'}
        className={cn('flex items-center gap-1', className)}
        role="alert"
      >
        <Clock className="w-3 h-3" />
        {isExpired ? 'Expired' : `Expires in ${timeDisplay}`}
      </Badge>
    )
  }

  // Banner variant (default)
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-4 py-3 rounded-lg border',
        isCritical 
          ? 'bg-red-50 border-red-500 text-red-800 dark:bg-red-950 dark:text-red-200' 
          : 'bg-yellow-50 border-yellow-500 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200',
        className
      )}
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
      <div className="flex items-center gap-2 text-sm">
        {isCritical ? (
          <>
            <span className="font-semibold">🔴 This session expires in {timeDisplay}!</span>
            <span>Save any important information.</span>
          </>
        ) : (
          <>
            <span>⚠️ This session expires in {timeDisplay}.</span>
            <span>Continue the conversation to extend.</span>
          </>
        )}
      </div>
    </div>
  )
}
